// Contesto globale dell'applicazione

import React from 'react';

// Importiamo i tipi dall'index
import { User, ExecutionUnit, Diary, Booking, Notification, Settings } from '../types';

// Definizione dei tipi per lo stato dell'applicazione
interface AppState {
    isAuthenticated: boolean;
    user: User | null;
    theme: 'light' | 'dark';
    executionUnits: ExecutionUnit[];
    diaries: Diary[];
    bookings: Booking[];
    notifications: Notification[];
    users: User[];
    syncStatus: 'idle' | 'syncing' | 'success' | 'error';
    lastSync: Date | null;
    settings: Settings;
}

// Definizione dei tipi per le azioni
type Action =
    | { type: 'LOGIN'; payload: User }
    | { type: 'LOGOUT' }
    | { type: 'TOGGLE_THEME' }
    | { type: 'SET_EXECUTION_UNITS'; payload: ExecutionUnit[] }
    | { type: 'ADD_EXECUTION_UNIT'; payload: ExecutionUnit }
    | { type: 'UPDATE_EXECUTION_UNIT'; payload: ExecutionUnit }
    | { type: 'DELETE_EXECUTION_UNIT'; payload: string }
    | { type: 'SET_DIARIES'; payload: Diary[] }
    | { type: 'ADD_DIARY'; payload: Diary }
    | { type: 'UPDATE_DIARY'; payload: Diary }
    | { type: 'DELETE_DIARY'; payload: string }
    | { type: 'SET_BOOKINGS'; payload: Booking[] }
    | { type: 'SET_NOTIFICATIONS'; payload: Notification[] }
    | { type: 'SET_USERS'; payload: User[] }
    | { type: 'SET_SYNC_STATUS'; payload: { status: 'idle' | 'syncing' | 'success' | 'error'; lastSync?: Date } }
    | { type: 'SET_SETTINGS'; payload: Settings };

// Stato iniziale dell'applicazione
const initialState: AppState = {
    isAuthenticated: false,
    user: null,
    theme: 'light',
    executionUnits: [],
    diaries: [],
    bookings: [],
    notifications: [],
    users: [],
    syncStatus: 'idle',
    lastSync: null,
    settings: {
        theme: 'light',
        language: 'it',
        notifications: true,
        syncFrequency: 30
    }
};

// Definizione del tipo del contesto
interface AppContextType {
    state: AppState;
    dispatch: (action: Action) => void;
}

// Creazione del contesto con valore di default
const AppContext = React.createContext<AppContextType>({
    state: initialState,
    dispatch: () => { }
});

// Reducer per la gestione dello stato
function appReducer(state: AppState, action: Action): AppState {
    switch (action.type) {
        case 'LOGIN':
            return {
                ...state,
                isAuthenticated: true,
                user: action.payload
            };
        case 'LOGOUT':
            return {
                ...state,
                isAuthenticated: false,
                user: null
            };
        case 'TOGGLE_THEME':
            return {
                ...state,
                theme: state.theme === 'light' ? 'dark' : 'light'
            };
        case 'SET_EXECUTION_UNITS':
            return {
                ...state,
                executionUnits: action.payload
            };
        case 'ADD_EXECUTION_UNIT':
            return {
                ...state,
                executionUnits: [...state.executionUnits, action.payload]
            };
        case 'UPDATE_EXECUTION_UNIT':
            return {
                ...state,
                executionUnits: state.executionUnits.map(unit =>
                    unit.id === action.payload.id ? action.payload : unit
                )
            };
        case 'DELETE_EXECUTION_UNIT':
            return {
                ...state,
                executionUnits: state.executionUnits.filter(unit => unit.id !== action.payload)
            };
        case 'SET_DIARIES':
            return {
                ...state,
                diaries: action.payload
            };
        case 'ADD_DIARY':
            return {
                ...state,
                diaries: [...state.diaries, action.payload]
            };
        case 'UPDATE_DIARY':
            return {
                ...state,
                diaries: state.diaries.map(diary =>
                    diary.id === action.payload.id ? action.payload : diary
                )
            };
        case 'DELETE_DIARY':
            return {
                ...state,
                diaries: state.diaries.filter(diary => diary.id !== action.payload)
            };
        case 'SET_BOOKINGS':
            return {
                ...state,
                bookings: action.payload
            };
        case 'SET_NOTIFICATIONS':
            return {
                ...state,
                notifications: action.payload
            };
        case 'SET_USERS':
            return {
                ...state,
                users: action.payload
            };
        case 'SET_SYNC_STATUS':
            return {
                ...state,
                syncStatus: action.payload.status,
                lastSync: action.payload.lastSync || state.lastSync
            };
        case 'SET_SETTINGS':
            return {
                ...state,
                settings: action.payload
            };
        default:
            return state;
    }
}

// Provider del contesto
interface AppProviderProps {
    children: React.ReactNode;
}

export const AppProvider = ({ children }: AppProviderProps) => {
    const [state, dispatch] = React.useReducer(appReducer, initialState);

    const contextValue: AppContextType = {
        state,
        dispatch
    };

    return (
        <AppContext.Provider value={contextValue}>
            {children}
        </AppContext.Provider>
    );
};

// Hook personalizzato per utilizzare il contesto
export const useAppContext = (): AppContextType => {
    const context = React.useContext(AppContext);
    return context;
};
